import { useState } from "react";
import { Card } from "../../components/atoms";
import { TgScreen } from "../../components/TgScreen";
import { Ico } from "../../lib/icons";
import { ImportSheet } from "./ImportSheet";
import { useWallet } from "../context";
import { haptic } from "../../lib/haptics";

/**
 * Shown when CloudStorage has a keystore blob but IndexedDB has no key
 * (cleared browser data, fresh install on a new device, etc).
 *
 * Two options:
 *  1. Paste mnemonic to restore — wallet is back, no funds lost
 *  2. Discard the orphan blob and start fresh
 */
export function RestoreSheet({
  reason,
  onDone,
}: {
  reason: string;
  onDone?: () => void;
}) {
  const [mode, setMode] = useState<"intro" | "import">("intro");
  const { reset } = useWallet();

  if (mode === "import") {
    return (
      <ImportSheet
        onBack={() => {
          haptic("light");
          setMode("intro");
        }}
        onDone={onDone}
      />
    );
  }

  return (
    <TgScreen title="Restore your wallet">
      <div style={{ padding: "12px 16px 24px" }}>
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <Ico.warning style={{ color: "var(--warning)", flexShrink: 0, marginTop: 3 }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Wallet found, but locked</div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--hint)",
                  marginTop: 6,
                  lineHeight: 1.5,
                }}
              >
                {reason}
              </div>
            </div>
          </div>
        </Card>

        <button
          onClick={() => {
            haptic("medium");
            setMode("import");
          }}
          style={{
            width: "100%",
            background: "var(--btn-primary-bg)",
            color: "var(--btn-primary-fg)",
            border: "none",
            borderRadius: 12,
            padding: "14px 16px",
            fontSize: 16,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "var(--font-text)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <span>Restore with 12-word phrase</span>
          <Ico.chevRight style={{ opacity: 0.7 }} />
        </button>

        <button
          onClick={async () => {
            haptic("medium");
            const confirmed = window.confirm(
              "Forget this wallet and start fresh? You'll lose access unless you have your mnemonic backed up.",
            );
            if (!confirmed) return;
            await reset();
          }}
          style={{
            width: "100%",
            background: "transparent",
            color: "var(--danger)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 12,
            padding: "12px 16px",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "var(--font-text)",
          }}
        >
          Forget and start fresh
        </button>
      </div>
    </TgScreen>
  );
}
